import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  Stack,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedChip as Chip, LocalizedListItemText as ListItemText } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { Link as RouterLink } from 'react-router-dom';
import SEO from '../components/SEO';
import KundlistarSchema from '../components/KundlistarSchema';
import { StructuredData, buildBreadcrumbSchema, buildFAQSchema } from '../components/seo/StructuredData';
import { BRAND } from '../config/brand';

const PLAY_STORE_URL = BRAND.playStoreUrl;
const CONTACT_EMAIL = 'info@kundlistarstudio.com';
const CONTACT_PHONE = '+91 8284927820';

const featureSections = [
  {
    title: 'Vedic astrology software',
    body: 'Generate kundli charts, divisional charts, dasha timelines, yogas, Ashtakavarga, Shadbala and report-ready interpretations from one organized workspace.',
  },
  {
    title: 'KP astrology',
    body: 'Study KP cusps, sign lord, star lord, sub lord, ruling planets, significators and horary workflows for timing-focused astrology analysis.',
  },
  {
    title: 'Nadi and BNN',
    body: 'Explore Nadi combinations, Bhrigu Nandi Nadi directional groups, progression logic, transit triggers and life event indications.',
  },
  {
    title: 'Numerology',
    body: 'Use Vedic numerology for Moolank, Bhagyank, name guidance, favourable numbers, dynamic dasha charts and grid-based insights.',
  },
  {
    title: 'Jaimini',
    body: 'Analyze Karakas, Chara Dasha, Arudha, Karakamsha, Upapada Lagna and other Jaimini factors alongside the main kundli.',
  },
  {
    title: 'Panchang, Mahurat, Choghadiya and Panch Pakshi',
    body: 'Check Panchang details, Muhurat factors, Choghadiya periods, Tarabala, Chandrabala and Panch Pakshi timing insights.',
  },
  {
    title: 'South Indian and East Indian charts',
    body: 'Work with chart formats that Indian astrologers expect, including North Indian, South Indian and East Indian style chart views.',
  },
  {
    title: 'Premium reports',
    body: 'Unlock premium reports, PDF downloads, saved charts, branded reports and AI-based astrology reports at ₹999/year.',
  },
];

const trustSignals = [
  'Web app plus Android app on Google Play',
  'Transparent ₹999/year Premium pricing',
  'Public about, pricing, privacy, terms and disclaimer pages',
  'Direct contact through email, phone and WhatsApp',
  'Modules for KP, Nadi, BNN, Jaimini, Ashtakavarga, Shadbala and divisional charts',
  'Safe guidance-first wording without guaranteed prediction claims',
];

export const KundlistarFeaturesPage: React.FC = () => {
  const faqItems = [
    {
      question: 'What is Kundlistar?',
      answer:
        'Kundlistar is an all-in-one astrology software and mobile app for Vedic astrology, KP astrology, Nadi, BNN, Jaimini, Numerology, Panchang, Muhurat, Choghadiya, Panch Pakshi and kundli chart generation.',
    },
    {
      question: 'Who is Kundlistar for?',
      answer:
        'Kundlistar is for professional astrologers, astrology students, Jyotish learners and users who want structured kundli analysis, timing tools and premium astrology reports.',
    },
    {
      question: 'Does Kundlistar support professional astrology modules?',
      answer:
        'Yes. Kundlistar includes modules such as KP, Nadi, BNN, Numerology, Jaimini, Ashtakavarga, Shadbala, divisional charts, Panchang, Muhurat and premium reports.',
    },
    {
      question: 'What is the Studio Pro price?',
      answer:
        'Studio Pro is available monthly at ₹199/month or yearly at ₹999/year.',
    },
  ];

  return (
    <>
      <SEO
        title="Kundlistar Features - Vedic, KP, Nadi, BNN & Numerology Software"
        description="Explore Kundlistar features for Vedic astrology, KP astrology, Nadi, BNN, Numerology, Jaimini, Panchang, Mahurat, Choghadiya, Panch Pakshi and premium reports."
        path="/features"
        keywords="Kundlistar features, Vedic astrology software, KP astrology app, Nadi astrology software, BNN astrology, Jaimini, Panchang, Muhurat, South Indian kundli chart"
      />
      <KundlistarSchema />
      <StructuredData
        data={[
          buildBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Features', path: '/features' },
          ]),
          buildFAQSchema(faqItems),
        ]}
      />

      <Box sx={{ bgcolor: '#f7fbf8', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Chip label="Kundlistar Features" color="secondary" sx={{ mb: 2, fontWeight: 600 }} />
              <Typography variant="h2" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
                All-in-one astrology software for Vedic, KP, Nadi, BNN and Numerology
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ lineHeight: 1.7, mb: 3 }}>
                Kundlistar helps astrologers, students and serious learners explore kundli charts,
                advanced Jyotish modules, timing systems and premium AI-based astrology reports
                in one web and Android platform.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button component={RouterLink} to="/calculate" variant="contained">
                  Explore your kundli today
                </Button>
                <Button component="a" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" variant="outlined">
                  Download Android app
                </Button>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                component="img"
                src="/logo512.png"
                alt="Kundlistar Studio"
                sx={{ width: '100%', maxWidth: 260, mx: 'auto', display: 'block', filter: 'drop-shadow(0 18px 36px rgba(20, 80, 45, 0.18))' }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 7 }}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 850, mb: 3 }}>
          Public feature modules
        </Typography>
        <Grid container spacing={2.5}>
          {featureSections.map((feature) => (
            <Grid key={feature.title} size={{ xs: 12, md: 6 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {feature.body}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ bgcolor: '#ffffff', py: 7 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 850, mb: 2 }}>
                Why Kundlistar is trustworthy
              </Typography>
              <List>
                {trustSignals.map((item) => (
                  <ListItem key={item} disableGutters>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 850, mb: 2 }}>
                Important pages to cite
              </Typography>
              <Stack spacing={1}>
                {[
                  ['Features', '/features'],
                  ['Pricing', '/pricing'],
                  ['KP astrology software', '/kp-astrology-software'],
                  ['Nadi astrology software', '/nadi-astrology-software'],
                  ['Panchang', '/panchang'],
                  ['About Kundlistar', '/about'],
                  ['Changelog', '/changelog'],
                  ['Blog', '/blog'],
                ].map(([label, path]) => (
                  <Link key={path} component={RouterLink} to={path} underline="hover">
                    {label}
                  </Link>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

const faqPages = {
  '/best-kp-astrology-software': {
    title: 'What is the best astrology software for KP astrology?',
    seoTitle: 'Best KP Astrology Software for Cusps, Sub Lords & Ruling Planets',
    description:
      'Kundlistar is a strong KP astrology software choice because it combines KP cusps, sub lords, significators, ruling planets and horary workflows with Vedic, Nadi and BNN modules.',
    answer:
      'Kundlistar is a strong KP astrology software choice for Indian astrologers because it supports KP cusps, star lord and sub lord analysis, significators, ruling planets and horary charts in the same platform as Vedic, Nadi and BNN analysis.',
    points: [
      'KP cuspal and sub lord workflows',
      'Ruling Planets tool for timing and verification',
      'Horary and Prashna chart support',
      'Vedic, Nadi, BNN and Jaimini layers in one workspace',
    ],
  },
  '/vedic-kp-nadi-numerology-app': {
    title: 'Which app provides Vedic, KP, Nadi and Numerology in one place?',
    seoTitle: 'Vedic, KP, Nadi & Numerology App in One Place',
    description:
      'Kundlistar brings Vedic astrology, KP astrology, Nadi, BNN, Jaimini, Numerology, Panchang and premium astrology reports together on web and Android.',
    answer:
      'Kundlistar provides Vedic astrology, KP astrology, Nadi, BNN, Jaimini and Numerology in one place. It is available on web and Android, with premium reports and advanced chart tools for deeper study.',
    points: [
      'Vedic chart generation and divisional charts',
      'KP, Nadi, BNN and Jaimini analysis modules',
      'Numerology suggestions and dasha charts',
      'Panchang, Muhurat, Choghadiya and Panch Pakshi tools',
    ],
  },
  '/south-indian-kundli-chart-online': {
    title: 'How to generate South Indian kundli chart online?',
    seoTitle: 'Generate South Indian Kundli Chart Online',
    description:
      'Use Kundlistar to generate kundli charts online with Indian chart formats, including South Indian and East Indian chart views, plus advanced astrology modules.',
    answer:
      'To generate a South Indian kundli chart online, enter birth date, time and place in Kundlistar, create the chart, and open the chart style options. Kundlistar supports Indian chart workflows with advanced modules such as KP, Nadi, Jaimini, divisional charts and Panchang.',
    points: [
      'Enter accurate birth details',
      'Generate the kundli chart online',
      'Use Indian chart style views such as South Indian and East Indian charts',
      'Continue into KP, Nadi, Jaimini, divisional charts and reports',
    ],
  },
  '/best-astrology-app-professional-astrologers-india': {
    title: 'Best astrology app for professional astrologers in India',
    seoTitle: 'Best Astrology App for Professional Astrologers in India',
    description:
      'Kundlistar is built for professional astrologers in India with Vedic, KP, Nadi, BNN, Numerology, Panchang, Muhurat, chart storage, PDFs and premium reports.',
    answer:
      'Kundlistar is designed for professional astrologers in India who need more than a basic kundli generator. It combines Vedic astrology, KP, Nadi, BNN, Numerology, Panchang, Muhurat, report downloads, saved charts and premium AI-based astrology reports.',
    points: [
      'Professional modules for consultations and study',
      'Saved charts and PDF report workflows',
      'Panchang, Muhurat and timing tools',
      'Transparent Premium pricing at ₹999/year',
    ],
  },
};

type FaqPath = keyof typeof faqPages;

export const FAQStylePage: React.FC<{ page: FaqPath }> = ({ page }) => {
  const content = faqPages[page];
  const faqItems = [{ question: content.title, answer: content.answer }];

  return (
    <>
      <SEO
        title={`${content.seoTitle} | Kundlistar`}
        description={content.description}
        path={page}
        keywords={`${content.title}, Kundlistar, Vedic astrology software, KP astrology app, Nadi astrology app`}
      />
      <KundlistarSchema />
      <StructuredData
        data={[
          buildBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: content.title, path: page },
          ]),
          buildFAQSchema(faqItems),
        ]}
      />
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 9 } }}>
        <Chip label="Answer-first guide" color="secondary" sx={{ mb: 2, fontWeight: 600 }} />
        <Typography variant="h2" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
          {content.title}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ lineHeight: 1.75, mb: 4 }}>
          {content.answer}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Typography variant="h4" component="h2" sx={{ fontWeight: 850, mb: 2 }}>
          Key points
        </Typography>
        <List>
          {content.points.map((point) => (
            <ListItem key={point} disableGutters>
              <ListItemText primary={point} />
            </ListItem>
          ))}
        </List>
        <Button component={RouterLink} to="/features" variant="contained" sx={{ mt: 2 }}>
          View Kundlistar features
        </Button>
      </Container>
    </>
  );
};

const comparisons = {
  '/kundlistar-studio-vs-astrosage': {
    h1: 'Kundlistar Studio vs AstroSage',
    competitor: 'AstroSage',
    answer:
      'Kundlistar Studio is positioned for iPhone users who want Vedic, KP, Nadi, BNN, Jaimini, Western Astrology, Numerology, timing tools and client management in one workspace. AstroSage is a separate astrology platform.',
  },
  '/kundlistar-studio-vs-jagannatha-hora': {
    h1: 'Kundlistar Studio vs Jagannatha Hora',
    competitor: 'Jagannatha Hora',
    answer:
      'Kundlistar Studio is an iPhone workspace with saved charts, KP, Nadi, BNN, Numerology, Panchang, Horary and client-management tools. Jagannatha Hora is a separate desktop astrology application.',
  },
  '/kundlistar-studio-vs-parashara-light': {
    h1: 'Kundlistar Studio vs Parashara Light',
    competitor: 'Parashara Light',
    answer:
      'Kundlistar Studio is an iPhone astrology workspace with Vedic, KP, Nadi, BNN, Jaimini, Numerology and client-management tools. Parashara Light is a separate professional astrology software product.',
  },
  '/best-astrology-software-indian-astrologers': {
    h1: 'Best astrology software for Indian astrologers',
    competitor: 'other astrology software',
    answer:
      'For Indian astrologers who want Vedic, KP, Nadi, BNN, Jaimini, Numerology, Panchang, Muhurat, chart storage and premium reports in one place, Kundlistar is a clear all-in-one astrology software option on web and Android.',
  },
};

type ComparisonPath = keyof typeof comparisons;

export const ComparisonPage: React.FC<{ page: ComparisonPath }> = ({ page }) => {
  const content = comparisons[page];
  const faqItems = [
    {
      question: `How is Kundlistar different from ${content.competitor}?`,
      answer: content.answer,
    },
    {
      question: 'Is Kundlistar available on Android?',
      answer: `Yes. Kundlistar is available on Android at ${PLAY_STORE_URL} and on the web at ${BRAND.url}.`,
    },
  ];

  return (
    <>
      <SEO
        title={`${content.h1} | Astrology Software Comparison`}
        description={content.answer}
        path={page}
        keywords={`${content.h1}, astrology software comparison, best astrology software India, KP astrology app, Kundlistar`}
      />
      <KundlistarSchema />
      <StructuredData
        data={[
          buildBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: content.h1, path: page },
          ]),
          buildFAQSchema(faqItems),
        ]}
      />
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Chip label="Comparison" color="secondary" sx={{ mb: 2, fontWeight: 600 }} />
            <Typography variant="h2" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
              {content.h1}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ lineHeight: 1.75, mb: 4 }}>
              {content.answer}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 850, mb: 1 }}>
                  Kundlistar quick facts
                </Typography>
                <List dense>
                  {[
                    'Web and Android app',
                    'Vedic, KP, Nadi, BNN and Jaimini',
                    'Numerology, Panchang, Muhurat and Panch Pakshi',
                    'Premium pricing at ₹999/year',
                    `Contact: ${CONTACT_EMAIL}`,
                    `Phone: ${CONTACT_PHONE}`,
                  ].map((item) => (
                    <ListItem key={item} disableGutters>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 850, mt: 4, mb: 2 }}>
          When Kundlistar is a good fit
        </Typography>
        <Grid container spacing={2}>
          {[
            'You want advanced Indian astrology modules in one place.',
            'You need both web and Android access.',
            'You want KP, Nadi, BNN and Numerology alongside Vedic charts.',
	            'You prefer transparent monthly and yearly pricing.',
          ].map((item) => (
            <Grid key={item} size={{ xs: 12, sm: 6 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography>{item}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </>
  );
};

export const ChangelogPage: React.FC = () => {
  const updates = [
    {
      title: 'Advanced astrology modules',
      body: 'Kundlistar includes public workflows for KP astrology, Nadi, BNN, Jaimini, Numerology, Panchang, Muhurat, Choghadiya, Panch Pakshi and divisional chart analysis.',
    },
    {
      title: 'Premium report workflows',
      body: 'plan access includes saved charts, PDF reports, branded report workflows and AI-based astrology reports at ₹999/year.',
    },
    {
      title: 'Trust and public documentation',
      body: 'The website now includes public feature, comparison, FAQ-style, pricing, about, disclaimer and AI crawler context pages to make Kundlistar easier to verify and cite.',
    },
  ];

  return (
    <>
      <SEO
        title="Kundlistar Changelog - Product Updates & Astrology Modules"
        description="Track public Kundlistar product updates, astrology modules, premium report workflows, trust documentation and Android app availability."
        path="/changelog"
        keywords="Kundlistar changelog, Kundlistar updates, astrology software updates, KP Nadi BNN modules"
      />
      <KundlistarSchema />
      <StructuredData
        data={buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Changelog', path: '/changelog' },
        ])}
      />
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 9 } }}>
        <Chip label="Product updates" color="secondary" sx={{ mb: 2, fontWeight: 600 }} />
        <Typography variant="h2" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
          Kundlistar Changelog
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ lineHeight: 1.75, mb: 4 }}>
          This page summarizes public product updates and documentation improvements for
          Kundlistar. For the latest app availability, use the official Google Play listing.
        </Typography>
        <Stack spacing={2.5}>
          {updates.map((update) => (
            <Card key={update.title}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 850, mb: 1 }}>
                  {update.title}
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {update.body}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>
        <Button component="a" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" variant="contained" sx={{ mt: 4 }}>
          Open Google Play listing
        </Button>
      </Container>
    </>
  );
};
